import React from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import ProductImage from "./ProductImage";
import { Badge, Skeleton } from "./Primitives";
import {
  availability,
  displayPrice,
  formatINR,
  minTenureLabel,
  priceUnit,
  productBadge,
  productPath,
} from "../../Service/catalog";

const CARD_SIZES = "(min-width: 1280px) 300px, (min-width: 768px) 33vw, 50vw";

function ProductCard({ product, priority = false, layout = "grid" }) {
  const path = productPath(product);
  const price = displayPrice(product);
  const stock = availability(product);
  const badge = productBadge(product);
  const tenure = minTenureLabel(product);
  const subCategory = product.subCategory && product.subCategory.name;
  const category = product.category && product.category.name;
  // Avoid "Premium · Premium Sofa" style repetition.
  const meta =
    category && subCategory && subCategory.toLowerCase().indexOf(category.toLowerCase()) !== -1
      ? subCategory
      : [category, subCategory].filter(Boolean).join(" · ");

  return (
    <article className={`v-card v-card--${layout} ${stock === "out" ? "is-out" : ""}`}>
      <div className="v-card__media">
        <ProductImage
          src={product.featuredImage}
          alt={product.name}
          fit="cover"
          priority={priority}
          sizes={CARD_SIZES}
        />
        <div className="v-card__flags">
          {stock === "out" ? (
            <Badge tone="solid">Out of stock</Badge>
          ) : badge ? (
            <Badge tone={badge.tone}>{badge.label}</Badge>
          ) : null}
        </div>
      </div>

      <div className="v-card__body">
        {meta ? <p className="v-card__meta">{meta}</p> : null}
        <h3 className="v-card__title">
          {path ? (
            <Link to={path} className="v-card__link">
              {product.name}
            </Link>
          ) : (
            product.name
          )}
        </h3>

        {stock === "in" ? (
          <p className="v-card__stock">
            <span className="v-card__dot" aria-hidden="true" />
            Available
          </p>
        ) : null}

        <div className="v-card__foot">
          <div>
            {price ? (
              <p className="v-card__price">
                <span className="v-card__from">From</span>
                <strong>{formatINR(price)}</strong>
                <span className="v-card__unit">/{priceUnit(product)}</span>
              </p>
            ) : (
              <p className="v-card__price v-card__price--quote">Price on request</p>
            )}
            {tenure ? <p className="v-card__tenure">Min. tenure {tenure}</p> : null}
          </div>
          <span className="v-card__cta" aria-hidden="true">
            <Icon name="arrowRight" size={18} />
          </span>
        </div>
      </div>
    </article>
  );
}

export function ProductCardSkeleton({ layout = "grid" }) {
  return (
    <div className={`v-card v-card--${layout} v-card--skeleton`} aria-hidden="true">
      <div className="v-card__media">
        <Skeleton className="v-card__skeleton-media" />
      </div>
      <div className="v-card__body">
        <Skeleton className="v-skeleton--text" width="40%" />
        <Skeleton className="v-skeleton--text" width="85%" height="1.1em" />
        <Skeleton className="v-skeleton--text" width="30%" />
        <div className="v-card__foot">
          <Skeleton className="v-skeleton--text" width="45%" height="1.3em" />
        </div>
      </div>
    </div>
  );
}

export default React.memo(ProductCard);
